import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollText, Pencil, Plus } from "lucide-react";
import { AutoTextEditor } from "@/components/AutoTextEditor";
import { CustomTextForm } from "@/components/CustomTextForm";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/roles";

const State = ({ on }) => on === null
  ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">When triggered</span>
  : on ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">On</span>
    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Off</span>;

const TextCard = ({ t, i, canEdit }) => {
  const [editing, setEditing] = useState(false);
  return (
  <div data-testid={`auto-text-${i}`} className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm font-semibold text-[#0B1F44]">{t.name}{t.custom && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Added by staff</span>}{t.edited && <span className="ml-2 rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-700">Edited</span>}</p>
      <div className="flex items-center gap-2"><State on={t.on} />
        {canEdit && t.key && !editing && <Button size="sm" variant="outline" className="h-7" onClick={() => setEditing(true)} data-testid={`auto-text-edit-${t.custom ? `custom-${t.custom.id}` : t.key}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>}</div>
    </div>
    <p className="mt-0.5 text-xs text-slate-500">{t.when}</p>
    {editing ? (t.custom ? <CustomTextForm initial={t.custom} phone={t.sample?.phone} onDone={() => setEditing(false)} /> : <AutoTextEditor t={t} onDone={() => setEditing(false)} />) : <>
    <div className="mt-2 whitespace-pre-wrap rounded-2xl rounded-tl-sm bg-slate-100 px-3.5 py-2.5 text-sm text-slate-800" data-testid={`auto-text-body-${i}`}>{t.text}</div>
    <p className="mt-1 text-[11px] text-slate-400">{[...t.text].length} characters{canEdit && t.edit_where ? ` · change this in ${t.edit_where}` : ""}</p></>}
  </div>
  );
};

export const AutomaticTexts = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "messaging");
  const { data } = useQuery({ queryKey: ["automatic-texts"], enabled: open, queryFn: async () => (await api.get("/automatic-texts")).data });
  const items = (data?.items || []).filter((t) => `${t.name} ${t.text}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="auto-texts-open-btn"><ScrollText className="mr-2 h-4 w-4" />Automatic texts</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="auto-texts-dialog">
          <DialogHeader>
            <DialogTitle>All automatic texts</DialogTitle>
            <DialogDescription>Exactly what patients receive, with sample details (Michael, Consultation, Thu Nov 6 at 2:30 PM) where the real ones are filled in.</DialogDescription>
          </DialogHeader>
          {data?.paused && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="auto-texts-paused">All texts are paused right now, so none of these will send until texts are resumed in SMS Inbox.</p>}
          {data?.quiet_backwards && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="auto-texts-quiet-warning">Quiet hours are set backwards ({data.quiet[0]}:00 to {data.quiet[1]}:00), so none of these texts can send. Fix it in SMS Inbox › SMS Automation Settings.</p>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input placeholder="Search texts" value={q} onChange={(e) => setQ(e.target.value)} data-testid="auto-texts-search" />
            {canEdit && !adding && <Button className="shrink-0 bg-[#0B1F44] hover:bg-[#13306a]" onClick={() => setAdding(true)} data-testid="auto-texts-add-btn"><Plus className="mr-1 h-4 w-4" />New automatic text</Button>}
          </div>
          {adding && <div className="rounded-xl border border-cyan-200 bg-cyan-50/30 p-4" data-testid="auto-texts-new-card"><p className="text-sm font-semibold text-[#0B1F44]">New automatic text</p><CustomTextForm phone={data?.clinic_phone} onDone={() => setAdding(false)} /></div>}
          <div className="space-y-3">
            {!data && <p className="text-sm text-slate-500">Loading…</p>}
            {data && !items.length && <p className="text-sm text-slate-500" data-testid="auto-texts-empty">No texts match your search.</p>}
            {items.map((t, i) => <TextCard key={t.key || t.name} t={t} i={i} canEdit={canEdit} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
